import type { NarrativeContext, NarrativeResult } from "../../narrative";
import type { LifeRecord, LifeGlance } from "../../types";
import { buildDivergentLife } from "./divergent.life";
import { buildAscendantLife } from "./ascendant.life";
import { buildStrangeLife } from "./strange.life";
import { buildUnexpectedLife } from "./unexpected.life";

export { buildDivergentLife } from "./divergent.life";
export { buildAscendantLife } from "./ascendant.life";
export { buildStrangeLife } from "./strange.life";
export { buildUnexpectedLife } from "./unexpected.life";
export { buildFallbackLife } from "./fallback";
export * from "./lifeHelpers";

/**
 * Builds the complete LifeRecord for an alternate self based on its archetype.
 */
export function buildLife(ctx: NarrativeContext, base: NarrativeResult): LifeRecord {
  switch (ctx.archetype) {
    case "divergent":
      return buildDivergentLife(ctx, base);
    case "ascendant":
      return buildAscendantLife(ctx, base);
    case "strange":
      return buildStrangeLife(ctx, base);
    case "unexpected":
      return buildUnexpectedLife(ctx, base);
  }
}

/**
 * Derives the free LifeGlance summary preview from a full LifeRecord.
 * Each field is guaranteed to be a short phrase (<= 90 chars).
 */
export function buildLifeGlance(life: LifeRecord): LifeGlance {
  const age = life.identity.age;

  // home from home.dwelling trimmed to first clause <= 90 chars
  const rawDwelling = life.home.dwelling.trim();
  const clauseMatch = rawDwelling.match(/^([^,;.\n]+)/);
  const clause = (clauseMatch ? clauseMatch[1] : rawDwelling).trim();
  const home = clause.length > 90 ? clause.slice(0, 87).trim() + "..." : clause;

  // society from society.summary first sentence trimmed to <= 90 chars
  const rawSociety = life.society.summary.trim();
  const sentenceMatch = rawSociety.match(/^([^.?!\n]+)/);
  const sentence = (sentenceMatch ? sentenceMatch[1] : rawSociety).trim();
  const society = sentence.length > 90 ? sentence.slice(0, 87).trim() + "..." : sentence;

  // standing from standing.wealthBracket + " · " + standing.socialClass trimmed
  const rawStanding = `${life.standing.wealthBracket} · ${life.standing.socialClass}`.trim();
  const standing = rawStanding.length > 90 ? rawStanding.slice(0, 87).trim() + "..." : rawStanding;

  return { age, home, society, standing };
}
